import type { NavItem } from '@/components/layout/nav-items';

/**
 * Befehlsgrammatik der Sprachsteuerung.
 *
 * Reine Funktionen ohne React und ohne Browser — die Erkennung ist damit
 * vollständig testbar (befehle.spec.ts), und die Oberfläche entscheidet nur
 * noch, was mit einem erkannten Befehl passiert.
 *
 * Grundsätze:
 *  - TOLERANT statt exakt. Wer „geh mal zu den Projekten bitte“ sagt, meint
 *    dasselbe wie „Projekte“. Einleitungen, Artikel und Füllwörter fliegen
 *    raus, danach entscheidet ein Ähnlichkeitsmaß (Tippfehler der Erkennung:
 *    „Projeckte“, „Prüfer“ / „Pruefer“).
 *  - NAVIGATIONSZIELE KOMMEN AUS nav-items.ts. Zweite Liste hieße doppelte
 *    Pflege; ein neuer Menüpunkt ist damit automatisch ansprechbar.
 *  - AUSFÜHRENDE BEFEHLE BRAUCHEN EINE BESTÄTIGUNG. Spracherkennung verhört
 *    sich; in einer Fachanwendung darf ein Missverständnis nichts auslösen.
 *    Reine Navigation ist folgenlos und läuft ohne Rückfrage.
 */

export type BefehlsAktion =
  | 'projekt-anlegen'
  | 'schnellsuche-oeffnen'
  | 'zurueck'
  | 'design-umschalten'
  | 'seitenleiste-umschalten'
  | 'abmelden'
  | 'sprachsteuerung-beenden';

export type ErkannterBefehl =
  | {
      art: 'navigation';
      id: string;
      beschriftung: string;
      ziel: string;
      bestaetigungNoetig: false;
      guete: number;
    }
  | {
      art: 'suche';
      id: string;
      beschriftung: string;
      begriff: string;
      bestaetigungNoetig: false;
      guete: number;
    }
  | {
      art: 'aktion';
      id: string;
      beschriftung: string;
      aktion: BefehlsAktion;
      bestaetigungNoetig: boolean;
      guete: number;
    };

export type Befehlserkennung =
  | { status: 'treffer'; befehl: ErkannterBefehl }
  | { status: 'unklar'; gehoert: string; vorschlaege: string[] };

/** Ein per Sprache erreichbarer Bereich. */
export interface NavigationsZiel {
  route: string;
  label: string;
  /** Weitere gesprochene Bezeichnungen für denselben Bereich. */
  synonyme: string[];
}

/**
 * Gesprochene Zweitbezeichnungen je Route. Die Menü-Labels allein reichen
 * nicht: niemand sagt „Prüfsachverständige“, wenn „Prüfer“ genügt.
 */
const ZUSATZ_SYNONYME: Record<string, string[]> = {
  '/dashboard': ['startseite', 'übersicht', 'start', 'überblick'],
  '/projects': ['projekt', 'projektliste', 'projektübersicht', 'nachweise'],
  '/ai-proposals': ['vorschläge', 'ki', 'ki vorschläge', 'feldvorschläge'],
  // „Ansprechpartner" hat keinen eigenen Menüpunkt mehr; die Wörter führen
  // in den Kundenbereich, von dem aus die Gesamtliste erreichbar ist.
  '/customers': [
    'kunde',
    'kundenliste',
    'auftraggeber',
    'bauherren',
    'kontakte',
    'kontakt',
    'ansprechpartner',
    'personen',
  ],
  '/pruefer': ['prüfer', 'sachverständige', 'prüfingenieure'],
  // Dokumente und E-Mails liegen seit der Zusammenführung unter „Eingang".
  // Die alten Wörter müssen weiter verstanden werden — niemand lernt um, nur
  // weil sich ein Menüpunkt geändert hat.
  '/documents': [
    'dokument',
    'dokumente',
    'dateien',
    'unterlagen',
    'pläne',
    'mail',
    'mails',
    'posteingang',
    'e-mails',
    'nachrichten',
  ],
  '/templates': ['vorlage', 'muster'],
  '/admin/users': ['benutzerverwaltung', 'nutzer', 'mitarbeiter'],
  '/admin/audit': ['protokoll', 'änderungsprotokoll', 'historie'],
  '/admin/rules': ['regeln', 'prüfregeln'],
  '/admin/ai-usage': ['ki nutzung', 'ki verbrauch', 'verbrauch'],
  '/admin/features': ['features', 'funktionen', 'feature flags'],
  '/admin/branding': ['branding', 'erscheinungsbild', 'logo'],
  '/hilfe': ['handbuch', 'anleitung', 'support', 'schulung'],
  '/settings': ['einstellung', 'optionen', 'konfiguration', 'profil'],
};

/** Baut die Sprachziele aus den Menüeinträgen (bereits nach Rechten gefiltert). */
export function navigationszieleAus(items: NavItem[]): NavigationsZiel[] {
  return items.map((item) => ({
    route: item.to,
    label: item.label,
    synonyme: ZUSATZ_SYNONYME[item.to] ?? [],
  }));
}

/** Einleitende Wendungen, die vor dem eigentlichen Ziel stehen. */
const EINLEITUNGEN = [
  'ich möchte zu',
  'ich will zu',
  'bring mich zu',
  'bringe mich zu',
  'navigiere zu',
  'navigiere nach',
  'wechsle zu',
  'wechsel zu',
  'springe zu',
  'spring zu',
  'geh mal zu',
  'gehe mal zu',
  'gehe zu',
  'geh zu',
  'gehe auf',
  'geh auf',
  'gehe in',
  'geh in',
  'zeig mir',
  'zeige mir',
  'mach auf',
  'ruf auf',
  'rufe auf',
  'öffne',
  'oeffne',
  'öffnen',
  'zeige',
  'zeig',
  'wechseln',
];

/** Wörter ohne Bedeutung für die Befehlserkennung. */
const FUELLWOERTER = new Set([
  'bitte',
  'mal',
  'jetzt',
  'doch',
  'danke',
  'mir',
  'die',
  'der',
  'das',
  'den',
  'dem',
  'ein',
  'eine',
  'einen',
  'einem',
  'einer',
  'zu',
  'zur',
  'zum',
  'nach',
  'auf',
  'in',
  'ins',
  'im',
  'meine',
  'meinen',
  'mein',
  'alle',
  'allen',
  'seite',
]);

/** Wortstämme, die eine Suche einleiten. */
const SUCH_EINLEITUNGEN = [
  'suche nach',
  'such nach',
  'suchen nach',
  'finde',
  'find',
  'suche',
  'such',
];

interface AktionsMuster {
  id: string;
  aktion: BefehlsAktion;
  beschriftung: string;
  bestaetigungNoetig: boolean;
  phrasen: string[];
}

/**
 * Feste Aktionsbefehle.
 *
 * „Neues Projekt“ ist bewusst bestätigungspflichtig, obwohl es „nur“ zum
 * Assistenten springt: ein verhörtes „neues Projekt“ mitten im Ausfüllen eines
 * Formulars würde die begonnene Eingabe verlassen.
 */
const AKTIONEN: AktionsMuster[] = [
  {
    id: 'aktion:projekt-anlegen',
    aktion: 'projekt-anlegen',
    beschriftung: 'Neues Projekt anlegen',
    bestaetigungNoetig: true,
    phrasen: [
      'neues projekt',
      'neues projekt anlegen',
      'projekt anlegen',
      'projekt erstellen',
      'projekt neu',
      'neuen nachweis anlegen',
      'neuer brandschutznachweis',
    ],
  },
  {
    id: 'aktion:abmelden',
    aktion: 'abmelden',
    beschriftung: 'Abmelden',
    bestaetigungNoetig: true,
    phrasen: ['abmelden', 'ausloggen', 'logout', 'melde mich ab'],
  },
  {
    id: 'aktion:schnellsuche',
    aktion: 'schnellsuche-oeffnen',
    beschriftung: 'Schnellsuche öffnen',
    bestaetigungNoetig: false,
    phrasen: ['schnellsuche', 'suchfeld', 'suchfenster', 'suche öffnen', 'schnellsuche öffnen'],
  },
  {
    id: 'aktion:zurueck',
    aktion: 'zurueck',
    beschriftung: 'Eine Seite zurück',
    bestaetigungNoetig: false,
    phrasen: ['zurück', 'zurueck', 'geh zurück', 'eine seite zurück', 'vorherige seite'],
  },
  {
    id: 'aktion:design',
    aktion: 'design-umschalten',
    beschriftung: 'Hell/Dunkel umschalten',
    bestaetigungNoetig: false,
    phrasen: [
      'dunkelmodus',
      'nachtmodus',
      'dark mode',
      'hellmodus',
      'light mode',
      'design umschalten',
    ],
  },
  {
    id: 'aktion:seitenleiste',
    aktion: 'seitenleiste-umschalten',
    beschriftung: 'Seitenleiste ein-/ausklappen',
    bestaetigungNoetig: false,
    phrasen: [
      'seitenleiste einklappen',
      'seitenleiste ausklappen',
      'menü einklappen',
      'menü ausklappen',
      'navigation einklappen',
    ],
  },
  {
    id: 'aktion:beenden',
    aktion: 'sprachsteuerung-beenden',
    beschriftung: 'Sprachsteuerung beenden',
    bestaetigungNoetig: false,
    phrasen: [
      'stopp',
      'stop',
      'beenden',
      'sprachsteuerung beenden',
      'mikrofon aus',
      'ruhe',
      'das wars',
    ],
  },
];

/** Beispiele für die Hilfe-Anzeige — was man sagen kann. */
export const BEFEHLS_BEISPIELE: { gruppe: string; beispiele: string[] }[] = [
  {
    gruppe: 'Navigation',
    beispiele: ['Gehe zu Projekten', 'Öffne das Dashboard', 'Zeig mir die Kunden', 'Hilfe'],
  },
  {
    gruppe: 'Suchen',
    beispiele: ['Suche nach Musterstraße', 'Finde Meier'],
  },
  {
    gruppe: 'Aktionen',
    beispiele: ['Neues Projekt', 'Eine Seite zurück', 'Dunkelmodus', 'Stopp'],
  },
];

/** Kleinschreiben, Satzzeichen weg, Mehrfach-Leerzeichen zusammenziehen. */
export function normalisiereBefehl(text: string): string {
  return text
    .toLowerCase()
    .replace(/[.,;:!?"„“()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Umlaute falten — die Erkennung liefert mal „Prüfer“, mal „Pruefer“. */
function falte(text: string): string {
  return text
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9 ]/g, '');
}

/** Levenshtein-Distanz (kurze Zeichenketten, zwei Zeilen genügen). */
function distanz(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  let vorher = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const jetzt = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const kosten = a[i - 1] === b[j - 1] ? 0 : 1;
      jetzt[j] = Math.min(jetzt[j - 1]! + 1, vorher[j]! + 1, vorher[j - 1]! + kosten);
    }
    vorher = jetzt;
  }
  return vorher[b.length]!;
}

/** 0..1 — 1 bedeutet identisch (nach Umlautfaltung). */
export function aehnlichkeit(a: string, b: string): number {
  const links = falte(a);
  const rechts = falte(b);
  if (links === '' || rechts === '') return 0;
  if (links === rechts) return 1;
  const maxLaenge = Math.max(links.length, rechts.length);
  return 1 - distanz(links, rechts) / maxLaenge;
}

/**
 * Ab hier gilt eine Wortfolge als gemeint. 0.72 lässt Tippfehler der Erkennung
 * durch („projeckte“ → „projekte“), weist aber Fremdwörter ab.
 */
const SCHWELLE = 0.72;
/** Ab hier taugt etwas noch als Vorschlag („Meinten Sie …“). */
const VORSCHLAGS_SCHWELLE = 0.4;

/** Entfernt Einleitung, Artikel und Füllwörter. */
function kernAussage(normalisiert: string): string {
  let rest = normalisiert;
  for (const einleitung of EINLEITUNGEN) {
    if (rest === einleitung) return '';
    if (rest.startsWith(`${einleitung} `)) {
      rest = rest.slice(einleitung.length + 1);
      break;
    }
  }
  return rest
    .split(' ')
    .filter((wort) => wort !== '' && !FUELLWOERTER.has(wort))
    .join(' ');
}

/** Bester Ähnlichkeitswert gegen eine Liste von Schreibweisen. */
function besteGuete(eingabe: string, varianten: string[]): number {
  let beste = 0;
  for (const variante of varianten) {
    const wert = aehnlichkeit(eingabe, variante);
    if (wert > beste) beste = wert;
    // Enthaltensein als ganzes Wort zählt fast wie ein Volltreffer — „kunden
    // liste anzeigen“ soll „Kunden“ finden, obwohl die Distanz groß ist.
    if (
      wert < 0.95 &&
      new RegExp(`(^| )${escapeRegExp(falte(variante))}( |$)`).test(falte(eingabe))
    ) {
      beste = Math.max(beste, 0.9);
    }
  }
  return beste;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export interface BefehlsKontext {
  /** Erreichbare Bereiche — vom Aufrufer nach Berechtigung gefiltert. */
  navigationsziele: NavigationsZiel[];
}

/**
 * Deutet eine erkannte Äußerung als Befehl.
 * Reihenfolge: Suche → Aktion → Navigation → unklar.
 */
export function erkenneBefehl(gesprochen: string, kontext: BefehlsKontext): Befehlserkennung {
  const normalisiert = normalisiereBefehl(gesprochen);
  if (normalisiert === '') {
    return { status: 'unklar', gehoert: gesprochen.trim(), vorschlaege: alleBeispiele() };
  }

  const suche = alsSuche(normalisiert);
  if (suche) return { status: 'treffer', befehl: suche };

  const kern = kernAussage(normalisiert);
  if (kern === '') {
    return { status: 'unklar', gehoert: gesprochen.trim(), vorschlaege: alleBeispiele() };
  }

  const aktion = alsAktion(kern);
  const navigation = alsNavigation(kern, kontext.navigationsziele);

  // Beide Kandidaten gegeneinander — „Hilfe“ ist Navigation, „Stopp“ Aktion.
  const beste = [aktion, navigation]
    .filter((k): k is ErkannterBefehl => k !== null)
    .sort((a, b) => b.guete - a.guete)[0];

  if (beste && beste.guete >= SCHWELLE) return { status: 'treffer', befehl: beste };

  return {
    status: 'unklar',
    gehoert: gesprochen.trim(),
    vorschlaege: vorschlaegeFuer(kern, kontext.navigationsziele),
  };
}

function alsSuche(normalisiert: string): ErkannterBefehl | null {
  for (const einleitung of SUCH_EINLEITUNGEN) {
    if (!normalisiert.startsWith(`${einleitung} `)) continue;
    const begriff = normalisiert.slice(einleitung.length + 1).trim();
    if (begriff === '') break;
    return {
      art: 'suche',
      id: 'suche',
      beschriftung: `Suchen nach „${begriff}“`,
      begriff,
      bestaetigungNoetig: false,
      guete: 1,
    };
  }
  return null;
}

function alsAktion(kern: string): ErkannterBefehl | null {
  let beste: ErkannterBefehl | null = null;
  for (const muster of AKTIONEN) {
    const guete = besteGuete(kern, muster.phrasen);
    if (guete < SCHWELLE) continue;
    if (beste && beste.guete >= guete) continue;
    beste = {
      art: 'aktion',
      id: muster.id,
      beschriftung: muster.beschriftung,
      aktion: muster.aktion,
      bestaetigungNoetig: muster.bestaetigungNoetig,
      guete,
    };
  }
  return beste;
}

function alsNavigation(kern: string, ziele: NavigationsZiel[]): ErkannterBefehl | null {
  let beste: ErkannterBefehl | null = null;
  for (const ziel of ziele) {
    const guete = besteGuete(kern, [ziel.label.toLowerCase(), ...ziel.synonyme]);
    if (guete < SCHWELLE) continue;
    if (beste && beste.guete >= guete) continue;
    beste = {
      art: 'navigation',
      id: `navigation:${ziel.route}`,
      beschriftung: `${ziel.label} öffnen`,
      ziel: ziel.route,
      bestaetigungNoetig: false,
      guete,
    };
  }
  return beste;
}

/** Nahe Treffer als „Meinten Sie …“, sonst die allgemeinen Beispiele. */
function vorschlaegeFuer(kern: string, ziele: NavigationsZiel[]): string[] {
  const kandidaten: { text: string; guete: number }[] = [];
  for (const ziel of ziele) {
    kandidaten.push({
      text: `Gehe zu ${ziel.label}`,
      guete: besteGuete(kern, [ziel.label.toLowerCase(), ...ziel.synonyme]),
    });
  }
  for (const muster of AKTIONEN) {
    kandidaten.push({ text: muster.beschriftung, guete: besteGuete(kern, muster.phrasen) });
  }
  const nah = kandidaten
    .filter((k) => k.guete >= VORSCHLAGS_SCHWELLE)
    .sort((a, b) => b.guete - a.guete)
    .slice(0, 3)
    .map((k) => k.text);
  return nah.length > 0 ? nah : alleBeispiele();
}

function alleBeispiele(): string[] {
  return BEFEHLS_BEISPIELE.flatMap((gruppe) => gruppe.beispiele).slice(0, 4);
}

/** Antwort auf eine Rückfrage — „ja“ führt aus, „nein“ verwirft. */
export function erkenneBestaetigung(gesprochen: string): 'ja' | 'nein' | null {
  const kern = normalisiereBefehl(gesprochen)
    .split(' ')
    .filter((wort) => !FUELLWOERTER.has(wort))
    .join(' ');
  const JA = [
    'ja',
    'jawohl',
    'jep',
    'genau',
    'richtig',
    'ok',
    'okay',
    'bestätigen',
    'ausführen',
    'mach das',
  ];
  const NEIN = [
    'nein',
    'ne',
    'nö',
    'abbrechen',
    'stopp',
    'stop',
    'nicht',
    'falsch',
    'verwerfen',
    'lass es',
  ];
  if (JA.some((wort) => besteGuete(kern, [wort]) >= 0.85)) return 'ja';
  if (NEIN.some((wort) => besteGuete(kern, [wort]) >= 0.85)) return 'nein';
  return null;
}
